import type { getOneOrderOutput } from "@/api/custom_types";
import { useApiError } from "@/hooks";
import { useTranslation } from "@/hooks/useTranslation";
import RelationNameLink from "@/pages/relations/RelationNameLink";
import { useGetOrders } from "@/services";
import { getTimeSinceText } from "@/utils/timestamp";
import { formatNumber } from "@/utils/tools";
import { DataTable, useServerTableState } from "@st-marketplace/ui";
import type { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";

interface OrderDetailHistoryProps {
	instanceId: string;
}

const OrderDetailsHistory: React.FC<OrderDetailHistoryProps> = ({
	instanceId,
}) => {
	const { t, lang } = useTranslation();
	const { getErrorInterface } = useApiError();

	const server = useServerTableState();

	const {
		data: orders,
		isLoading,
		isFetching,
		error,
		refetch,
	} = useGetOrders({
		query: {
			page: server.pageIndex + 1,
			page_size: server.pageSize,
			origin_instance_id: instanceId,
		},
	});

	const columns = useMemo<ColumnDef<getOneOrderOutput>[]>(
		() => [
			{
				id: "amount",
				header: t("orderInfo.history.tableAmount"),
				enableSorting: false,
				cell: ({ row }) =>
					`${formatNumber(row.original.amount / 1000, "count")}${t(
						"signs.thousands",
					)}`,
			},
			{
				id: "buyer_name",
				header: t("orderInfo.history.tableBuyer"),
				enableSorting: false,
				cell: ({ row }) => (
					<RelationNameLink
						name={row.original.buyer_name}
						relationId={row.original.relation_id}
						party="buyer"
					/>
				),
			},
			{
				id: "seller_name",
				header: t("orderInfo.history.tableSeller"),
				enableSorting: false,
				cell: ({ row }) => (
					<RelationNameLink
						name={row.original.seller_name}
						relationId={row.original.relation_id}
						party="seller"
					/>
				),
			},
			{
				id: "unit_price",
				header: t("orderInfo.history.tableUnitPrice"),
				enableSorting: false,
				cell: ({ row }) => formatNumber(row.original.unit_price, "price"),
			},
			{
				id: "total_price",
				header: t("orderInfo.history.tableTotalPrice"),
				enableSorting: false,
				cell: ({ row }) => formatNumber(row.original.total_price, "price"),
			},
			{
				id: "created_date",
				header: t("orderInfo.history.tableCreatedAt"),
				enableSorting: false,
				cell: ({ row }) => getTimeSinceText(row.original.created_date, t, lang),
			},
		],
		[t, lang],
	);

	const rows = orders?.results ?? [];

	return (
		<DataTable
			columns={columns}
			data={rows}
			totalItems={orders?.count ?? 0}
			server={server}
			isLoading={isLoading}
			isFetching={isFetching}
			error={error ? getErrorInterface(error) : null}
			onRetry={refetch}
			getRowId={(row) => row.pk}
		/>
	);
};

export default OrderDetailsHistory;
